// src/components/SubscriptionManagement/ConsolidatedPlansScreen.js
//
// Unified subscription & plans screen (replaces CurrentPlan + PlansAndPricing).
// Shows a context-aware summary banner + three plan cards with a billing toggle,
// adapting to six states: trial, active, renewal_due, past_due, expired, new.

import React, { useState, useMemo } from "react";
import { Box, Grid } from "@mui/material";
import {
  getCurrencySymbol,
  getPlanFeatures,
  getPlanPrice,
  getPlanPricing,
  normalizeCycle,
  getAvailableBillingCycles,
  formatDate,
} from "./utils";

// ─── Design tokens ────────────────────────────────────────────────────────────

const ACCENT      = "#1f74e0";
const ACCENT_DARK = "#10295c";
const SOFT        = "#eaf2fb";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const hexA = (hex, a) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

const daysUntil = (dateStr) => {
  if (!dateStr) return Infinity;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
};

/**
 * Derive the display scenario from the subscription status + end_date.
 * Maps backend statuses to one of: trial | active | renewal_due | past_due | expired | new
 */
const deriveScenario = (sub) => {
  if (!sub) return "new";
  const s = sub.status?.toLowerCase();
  if (s === "trial") return "trial";
  if (s === "past_due") return "past_due";
  if (s === "suspended") return "suspended";
  if (s === "expired" || s === "cancelled") return "expired";
  if (s === "active") {
    return daysUntil(sub.end_date) <= 7 ? "renewal_due" : "active";
  }
  return "new";
};

const toDisplayName = (name) =>
  (name || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// ─── CTA config per (scenario × plan) ────────────────────────────────────────

/**
 * Returns { label, kind } for a plan card CTA.
 * kind: 'accent' | 'primary' | 'outline' | 'disabled'
 *   accent   → dark navy, primary action (Subscribe Now / Renew Now / Pay & Renew)
 *   primary  → blue, secondary action (Upgrade)
 *   outline  → bordered, tertiary action (Downgrade / Start Plan / Subscribe)
 *   disabled → grayed, not clickable (Current Plan)
 */
const getCtaConfig = (plan, scenario, currentPlan, recommendedId) => {
  const isCurrent     = plan.id === currentPlan?.id;
  const isRecommended = plan.id === recommendedId;
  const isHigher      =
    currentPlan
      ? getPlanPrice(plan, "annual") > getPlanPrice(currentPlan, "annual")
      : false;

  switch (scenario) {
    case "trial":
      return isCurrent
        ? { label: "Subscribe Now", kind: "accent" }
        : { label: "Start Plan",    kind: "outline" };

    case "active":
      if (isCurrent) return { label: "Current Plan", kind: "disabled", tick: true };
      return isHigher
        ? { label: "Upgrade",   kind: "disabled" }
        : { label: "Downgrade", kind: "disabled" };

    case "renewal_due":
      if (isCurrent) return { label: "Renew Now", kind: "accent" };
      return isHigher
        ? { label: "Upgrade",   kind: "disabled" }
        : { label: "Downgrade", kind: "disabled" };

    case "past_due":
    case "suspended":
      if (isCurrent) return { label: "Pay & Renew", kind: "accent" };
      return isHigher
        ? { label: "Upgrade",   kind: "disabled" }
        : { label: "Downgrade", kind: "disabled" };

    case "expired":
      return {
        label: "Subscribe",
        kind: isRecommended ? "accent" : "outline",
      };

    case "new":
    default:
      return {
        label: "Start Plan",
        kind: isRecommended ? "accent" : "outline",
      };
  }
};

const ctaStyle = (kind) => {
  const base = {
    width: "100%",
    borderRadius: "10px",
    padding: "13px",
    fontWeight: 700,
    fontSize: "14px",
    cursor: kind === "disabled" ? "default" : "pointer",
    fontFamily: "inherit",
    border: "none",
    transition: "background 0.15s",
  };
  switch (kind) {
    case "accent":   return { ...base, background: ACCENT_DARK, color: "#fff" };
    case "primary":  return { ...base, background: ACCENT,      color: "#fff" };
    case "outline":  return { ...base, background: "#fff", border: `1.5px solid ${ACCENT}`, color: ACCENT };
    case "disabled": return { ...base, background: "#f8fafc", border: "1.5px solid #e2e8f0", color: "#94a3b8" };
    default:         return { ...base, background: "#f8fafc", color: "#94a3b8" };
  }
};

// ─── StatCell ─────────────────────────────────────────────────────────────────

const StatCell = ({ label, value, sub }) => (
  <Box sx={{ flex: "1 1 110px", minWidth: 90 }}>
    <Box
      sx={{
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.1em",
        color: "rgba(255,255,255,0.6)",
        mb: "5px",
        textTransform: "uppercase",
      }}
    >
      {label}
    </Box>
    <Box sx={{ fontSize: "18px", fontWeight: 700, color: "#fff", mt: "5px" }}>
      {value}
    </Box>
    {sub && (
      <Box sx={{ fontSize: "11px", color: "rgba(255,255,255,0.55)" }}>
        {sub}
      </Box>
    )}
  </Box>
);

// ─── PlanCard ─────────────────────────────────────────────────────────────────

const PlanCard = ({
  plan,
  scenario,
  currentPlan,
  recommendedId,
  billing,
  currSym,
  onSelect,
  onRenewCurrentPlan,
}) => {
  const highlight = ["new", "expired"].includes(scenario)
    ? plan.id === recommendedId
    : plan.id === currentPlan?.id;

  const cta      = getCtaConfig(plan, scenario, currentPlan, recommendedId);
  const features = getPlanFeatures(plan);

  const priceEntry   = getPlanPricing(plan, billing);
  const priceAmt     = priceEntry ? parseFloat(priceEntry.price) : null;
  const monthlyEq    = billing === "annual" && priceAmt ? Math.round(priceAmt / 12) : null;
  const quarterlyAmt = getPlanPrice(plan, "quarterly");
  const annualAmt    = getPlanPrice(plan, "annual");
  const savingsAmt   = quarterlyAmt && annualAmt ? quarterlyAmt * 4 - annualAmt : 0;

  const fmt = (n) =>
    n != null ? `${currSym}${Number(n).toLocaleString("en-IN")}` : "—";

  const RADIUS = "16px";

  const cardSx = highlight
    ? {
        position: "relative",
        background: "#fff",
        border: `2px solid ${ACCENT}`,
        borderRadius: RADIUS,
        p: "22px 20px 20px",
        display: "flex",
        flexDirection: "column",
        boxShadow: `0 14px 34px ${hexA(ACCENT, 0.16)}`,
        height: "100%",
        width: "100%"
      }
    : {
        position: "relative",
        background: "#fff",
        border: "1px solid #e6e8ec",
        borderRadius: RADIUS,
        p: "22px 20px 20px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%"
      };

  // Status badge on the card
  let badge = null;
  if (scenario === "trial" && plan.id === currentPlan?.id) {
    badge = { text: "Trial plan",  bg: "#f59e0b",  color: "#fff" };
  } else if (["active", "renewal_due"].includes(scenario) && plan.id === currentPlan?.id) {
    badge = { text: "Current",     bg: "#16a34a",  color: "#fff" };
  } else if (scenario === "past_due" && plan.id === currentPlan?.id) {
    badge = { text: "Past Due",  bg: "#dc2626",  color: "#fff" };
  } else if (scenario === "suspended" && plan.id === currentPlan?.id) {
    badge = { text: "Suspended", bg: "#dc2626",  color: "#fff" };
  }

  // Determine the click handler: renew-in-place vs normal plan selection
  const isRenewAction = cta.label === "Renew Now" || cta.label === "Pay & Renew";
  const handleClick = () => {
    if (cta.kind === "disabled") return;
    if (isRenewAction && onRenewCurrentPlan) {
      onRenewCurrentPlan(plan);
    } else {
      onSelect(plan);
    }
  };

  return (
    <Box sx={cardSx}>
      {/* Top accent bar on highlighted card */}
      {highlight && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 4,
            right: 4,
            height: "5px",
            background: ACCENT,
            borderRadius: `${RADIUS} ${RADIUS} 0 0`,
          }}
        />
      )}

      {/* Header: monogram + name + badge */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
          minHeight: 36,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "9px",
              background: SOFT,
              color: ACCENT,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "15px",
              flexShrink: 0,
            }}
          >
            {(plan.name || "?")[0].toUpperCase()}
          </Box>
          <Box sx={{ fontSize: "18px", fontWeight: 800, color: "#0f172a" }}>
            {toDisplayName(plan.name)}
          </Box>
        </Box>
        {badge && (
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              px: "11px",
              py: "5px",
              borderRadius: "999px",
              background: badge.bg,
              color: badge.color,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {badge.text}
          </Box>
        )}
      </Box>

      {/* Tagline — only shown when description is present */}
      {plan.description && (
        <Box sx={{ fontSize: "13px", color: "#64748b", mt: "8px" }}>
          {plan.description}
        </Box>
      )}

      {/* Price */}
      <Box sx={{ mt: "12px" }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "3px" }}>
          <Box
            component="span"
            sx={{ fontSize: "34px", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}
          >
            {priceAmt != null ? fmt(priceAmt) : "N/A"}
          </Box>
          <Box
            component="span"
            sx={{ fontSize: "15px", color: "#94a3b8", fontWeight: 600 }}
          >
            {billing === "annual" ? "/year" : billing === "monthly" ? "/month" : "/quarter"}
          </Box>
        </Box>
        {monthlyEq != null && (
          <Box sx={{ fontSize: "12.5px", color: "#64748b", mt: "3px" }}>
            {fmt(monthlyEq)}/month
          </Box>
        )}
        {billing === "annual" && savingsAmt > 0 && (
          <Box sx={{ fontSize: "12.5px", color: "#16a34a", fontWeight: 700, mt: "4px" }}>
            You save {fmt(savingsAmt)}/yr
          </Box>
        )}
      </Box>

      {/* Divider */}
      <Box sx={{ height: "1px", background: "#eef1f5", my: "18px" }} />

      {/* Features */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "11px", flex: 1 }}>
        {features.map((f, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "#22c55e",
                color: "#fff",
                fontSize: "11px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              ✓
            </Box>
            <Box component="span" sx={{ fontSize: "13.5px", color: "#334155" }}>
              {f}
            </Box>
          </Box>
        ))}
      </Box>

      {/* CTA button */}
      <Box sx={{ mt: "24px" }}>
        <button
          style={ctaStyle(cta.kind)}
          disabled={cta.kind === "disabled"}
          onClick={handleClick}
        >
          {cta.tick ? `✓ ${cta.label}` : cta.label}
        </button>
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * ConsolidatedPlansScreen
 *
 * Props:
 *   currentSubscription   – subscription object from GET /tenants/subscriptions/
 *   currentAddons         – array of active addon objects
 *   plans                 – array of plan objects from GET /plans/
 *   selectedBillingCycle  – current billing cycle from parent
 *   billingCurrency       – "INR" | "USD" | …
 *   onBillingCycleChange  – (cycle) => void
 *   onPlanSelect          – (plan) => void  — normal plan selection (→ add-ons → checkout)
 *   onRenewCurrentPlan    – (plan) => void  — renew same plan directly (→ checkout, skipping add-ons)
 *   onManageAddons        – () => void | undefined
 *   onViewBillingHistory  – () => void | undefined
 *   loading               – boolean
 */
const ConsolidatedPlansScreen = ({
  currentSubscription,
  plans = [],
  selectedBillingCycle,
  billingCurrency = "INR",
  onBillingCycleChange,
  onPlanSelect,
  onRenewCurrentPlan,
  onManageAddons,
  onViewBillingHistory,
  loading = false,
}) => {
  const currSym  = getCurrencySymbol(billingCurrency);
  const sub      = currentSubscription;
  const scenario = useMemo(() => deriveScenario(sub), [sub]);

  // ── Billing toggle ─────────────────────────────────────────────────────────

  const availableCycles = getAvailableBillingCycles(plans);
  const initialCycle =
    normalizeCycle(selectedBillingCycle) ||
    normalizeCycle(sub?.billing_cycle) ||
    (availableCycles.includes("annual") ? "annual" : availableCycles[0] || "annual");

  const [billing, setBilling] = useState(initialCycle);

  const handleBillingChange = (cycle) => {
    setBilling(cycle);
    if (onBillingCycleChange) onBillingCycleChange(cycle);
  };

  // ── Plan ordering & recommended selection ──────────────────────────────────

  const sortedPlans = useMemo(
    () =>
      [...plans]
        .filter((p) => p.is_active)
        .sort((a, b) => getPlanPrice(a, "annual") - getPlanPrice(b, "annual")),
    [plans]
  );

  // Use middle-ish plan as "recommended" for new/expired states
  const recommendedId = useMemo(() => {
    if (sortedPlans.length === 0) return null;
    const idx = Math.min(1, sortedPlans.length - 1);
    return sortedPlans[idx].id;
  }, [sortedPlans]);

  const currentPlan = sub?.plan || null;

  // ── State flags ───────────────────────────────────────────────────────────

  const noPlan      = ["new", "expired"].includes(scenario);
  const hasPaidPlan = ["active", "renewal_due", "past_due", "suspended"].includes(scenario);
  const isTrial     = scenario === "trial";

  // ── Banner content ─────────────────────────────────────────────────────────

  const bannerTitle = {
    active:       `Current Plan: ${toDisplayName(currentPlan?.name)}`,
    renewal_due:  `Current Plan: ${toDisplayName(currentPlan?.name)}`,
    past_due:     `Current Plan: ${toDisplayName(currentPlan?.name)}`,
    suspended:    `Current Plan: ${toDisplayName(currentPlan?.name)}`,
    trial:        `Free Trial: ${toDisplayName(currentPlan?.name)}`,
    expired:      "Trial expired",
    new:          "Choose your plan",
  }[scenario];

  const statusLabel = {
    active: "ACTIVE", renewal_due: "ACTIVE", past_due: "PAST DUE",
    suspended: "SUSPENDED",
    trial: "TRIAL",   expired: "EXPIRED",    new: "NO PLAN",
  }[scenario];

  const bannerSubtitle = {
    active:      "Renew, switch tiers, or add add-ons — everything in one place.",
    renewal_due: "Renew, switch tiers, or add add-ons — everything in one place.",
    past_due:    "Your payment did not go through. Renew below to restore full access.",
    suspended:   "Your account has been suspended. Pay the outstanding amount to restore access.",
    trial:       `You are trialing ${toDisplayName(currentPlan?.name)}. Subscribe to keep your setup and team.`,
    expired:     "Your free trial has ended. Subscribe to a plan to regain access.",
    new:         "You do not have an active subscription yet. Pick a plan below to get started.",
  }[scenario];

  // Plan cost from sub.plan.pricing (AddonPricing for the internal tier addon),
  // which reflects the actual per-cycle charge the tenant was onboarded at.
  const planCostDisplay = (() => {
    if (noPlan) return "—";
    if (isTrial) return "N/A";
    const price = getPlanPrice(currentPlan, sub?.billing_cycle);
    return price > 0
      ? `${currSym}${price.toLocaleString("en-IN")}`
      : "N/A";
  })();
  const planCostUnit = isTrial
    ? ""
    : billing === "annual"
    ? "per year"
    : billing === "monthly"
    ? "per month"
    : "per quarter";

  // Add-on cost in the banner grid
  const addonCostDisplay = (() => {
    if (noPlan) return "—";
    const cycle    = sub?.billing_cycle;
    const addons   = sub?.addons || [];
    const qtys     = sub?.addon_quantities || {};
    if (!addons.length) return `${currSym}0`;
    let total = 0;
    addons.forEach((addon) => {
      const match = Array.isArray(addon?.pricing)
        ? addon.pricing.find(
            (p) => p.billing_cycle?.toLowerCase() === cycle?.toLowerCase() && p.is_active
          )
        : null;
      const unit = parseFloat(match?.price ?? addon?.price ?? 0);
      const qty  = parseInt(qtys[String(addon.id)] ?? 1, 10);
      total += unit * qty;
    });
    return `${currSym}${total.toLocaleString("en-IN")}`;
  })();

  const statusText = {
    active: "Active", renewal_due: "Active", past_due: "Past Due",
    suspended: "Suspended",
    trial: "Trial",   expired: "Expired",    new: "No plan",
  }[scenario];

  const daysLeft = daysUntil(sub?.end_date);
  const statusSub = {
    active:      "Auto-renewal",
    renewal_due: `Ends in ${Math.max(0, daysLeft)} days`,
    past_due:    "Overdue",
    suspended:   "Suspended",
    trial:       Number.isFinite(daysLeft) ? `Ends in ${Math.max(0, daysLeft)} days` : "Trial active",
    expired:     "No active plan",
    new:         "—",
  }[scenario];

  // Alert strip inside the banner
  const alertConfig = (() => {
    if (scenario === "renewal_due")
      return { kind: "amber", tag: "ENDING SOON",    text: `Your plan ends in ${Math.max(0, daysLeft)} days (${formatDate(sub?.end_date)}). Renew now to avoid interruption.` };
    if (scenario === "trial")
      return { kind: "amber", tag: "TRIAL ENDS SOON", text: `Your free trial ends in ${Math.max(0, daysLeft)} days (${formatDate(sub?.end_date)}). Subscribe to ${toDisplayName(currentPlan?.name)} to continue without interruption.` };
    if (scenario === "past_due")
      return { kind: "red",   tag: "PAST DUE",        text: "Payment failed — renew now to restore access for your team." };
    if (scenario === "suspended")
      return { kind: "red",   tag: "SUSPENDED",        text: "Your account is suspended due to an unpaid balance. Pay & renew to restore full access." };
    if (scenario === "expired")
      return { kind: "red",   tag: "TRIAL EXPIRED",   text: `Your free trial ended on ${formatDate(sub?.end_date)}. Subscribe to a plan to regain access.` };
    return null;
  })();

  const toneHex = { amber: "#f59e0b", red: "#ef4444" };
  const alert   = alertConfig ? { ...alertConfig, hex: toneHex[alertConfig.kind] } : null;

  // ── Loading state ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
        <Box
          sx={{
            borderRadius: "18px",
            height: 220,
            background: "linear-gradient(115deg, #2575c8 0%, #103f7a 100%)",
            opacity: 0.5,
          }}
        />
        <Box sx={{ background: "#fff", borderRadius: "18px", p: "30px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 200,
                height: 44,
                borderRadius: "12px",
                background: "#eef1f5",
              }}
            />
          </Box>
          <Grid container spacing={2.5}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box
                  sx={{
                    height: 460,
                    borderRadius: "16px",
                    background: "#f4f5f7",
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const heroBtnStyle = {
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.32)",
    color: "#fff",
    fontWeight: 600,
    fontSize: "13px",
    padding: "9px 16px",
    borderRadius: "10px",
    cursor: "pointer",
    fontFamily: "inherit",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>

      {/* ════════════════════════════════════════════════════════════════════
          Summary Banner
         ════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          borderRadius: "18px",
          padding: "26px 30px",
          color: "#fff",
          background: "linear-gradient(115deg, #2575c8 0%, #103f7a 100%)",
          boxShadow: `0 12px 30px ${hexA(ACCENT_DARK, 0.22)}`,
        }}
      >
        {/* Top row: title + status badge + action buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  fontSize: "23px",
                  fontWeight: 800,
                  letterSpacing: "-0.01em",
                  color: "#fff",
                }}
              >
                {bannerTitle}
              </Box>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  padding: "4px 11px",
                  borderRadius: "999px",
                  background: "rgba(255,255,255,0.16)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.4)",
                }}
              >
                {statusLabel}
              </Box>
            </Box>
            <Box
              sx={{
                fontSize: "13px",
                color: "rgba(255,255,255,0.74)",
                mt: "5px",
              }}
            >
              {bannerSubtitle}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {hasPaidPlan && onViewBillingHistory && (
              <Box
                component="button"
                onClick={onViewBillingHistory}
                sx={heroBtnStyle}
              >
                Download Invoice
              </Box>
            )}
            {(hasPaidPlan || isTrial) && onManageAddons && (
              <Box
                component="button"
                onClick={onManageAddons}
                sx={heroBtnStyle}
              >
                Manage Add-ons
              </Box>
            )}
          </Box>
        </Box>

        {/* Stats grid — hidden for 'new' state */}
        {!noPlan && (
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              mt: "24px",
              flexWrap: "wrap",
            }}
          >
            <StatCell
              label="Plan Cost"
              value={planCostDisplay}
              sub={planCostUnit}
            />
            <StatCell
              label="Add-on Cost"
              value={addonCostDisplay}
              sub={noPlan ? "" : billing === "annual" ? "per year" : billing === "monthly" ? "per month" : "per quarter"}
            />
            <StatCell
              label="Status"
              value={statusText}
              sub={statusSub}
            />
            <StatCell
              label="Onboarding Date"
              value={
                sub?.onboarding_date
                  ? formatDate(sub.onboarding_date)
                  : formatDate(sub?.start_date) || "—"
              }
            />
            <StatCell
              label="Start Date"
              value={formatDate(sub?.start_date) || "—"}
            />
            <StatCell
              label="End Date"
              value={formatDate(sub?.end_date) || "—"}
            />
            {sub?.max_users != null && (
              <StatCell
                label="Max Users"
                value={
                  sub.max_users.additional_max_users > 0
                    ? `${sub.max_users.plan_max_users}+${sub.max_users.additional_max_users}`
                    : String(sub.max_users.plan_max_users)
                }
                sub="users"
              />
            )}
          </Box>
        )}

        {/* Term note */}
        {(hasPaidPlan || isTrial) && (
          <Box
            sx={{
              mt: "16px",
              fontSize: "12px",
              color: "rgba(255,255,255,0.74)",
            }}
          >
            {billing === "annual" ? "Annual" : "Quarterly"} billing &nbsp;&bull;&nbsp; new term {formatDate(sub?.start_date)} – {formatDate(sub?.end_date)}
          </Box>
        )}

        {/* Alert strip */}
        {alert && (
          <Box
            sx={{
              mt: "20px",
              borderRadius: "12px",
              p: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              background: hexA(alert.hex, 0.16),
              border: `1px solid ${hexA(alert.hex, 0.5)}`,
            }}
          >
            <Box
              sx={{
                fontSize: "10px",
                fontWeight: 800,
                letterSpacing: "0.08em",
                px: "9px",
                py: "4px",
                borderRadius: "6px",
                flexShrink: 0,
                background: alert.hex,
                color: alert.kind === "red" ? "#fff" : "#1f2937",
              }}
            >
              {alert.tag}
            </Box>
            <Box
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: alert.kind === "red" ? "#fecaca" : "#fde68a",
              }}
            >
              {alert.text}
            </Box>
          </Box>
        )}
      </Box>

      {/* ════════════════════════════════════════════════════════════════════
          Plans Section
         ════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "18px",
          pt: "20px",
          pb: "16px",
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,.05)",
        }}
      >
        {/* Billing toggle — only interactive for trial / new / expired states */}
        {availableCycles.length > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: "24px", px: "16px" }}>
            <Box
              sx={{
                display: "inline-flex",
                background: "#eef1f5",
                borderRadius: "12px",
                p: "4px",
                gap: "4px",
              }}
            >
              {availableCycles.map((cycle) => {
                const isActive  = billing === cycle;
                const isAnnual  = cycle === "annual";
                const toggleable = isTrial || noPlan;
                return (
                  <Box
                    key={cycle}
                    component="button"
                    onClick={toggleable ? () => handleBillingChange(cycle) : undefined}
                    sx={{
                      border: "none",
                      fontWeight: 700,
                      fontSize: "13px",
                      px: "18px",
                      py: "9px",
                      borderRadius: "9px",
                      cursor: toggleable ? "pointer" : "default",
                      fontFamily: "inherit",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      background: isActive ? ACCENT_DARK : "transparent",
                      color: isActive ? "#fff" : (toggleable ? "#475569" : "#94a3b8"),
                      transition: "background 0.15s, color 0.15s",
                      opacity: !toggleable && !isActive ? 0.45 : 1,
                    }}
                  >
                    {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
                    {isAnnual && toggleable && (
                      <Box
                        component="span"
                        sx={{
                          background: "#16a34a",
                          color: "#fff",
                          fontSize: "10px",
                          fontWeight: 700,
                          px: "7px",
                          py: "2px",
                          borderRadius: "999px",
                        }}
                      >
                        Save 10%
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Plan cards grid */}
        <Grid container spacing={2} sx={{px: 4}}>
          {sortedPlans.map((plan) => (
            <Grid
              item
              xs={12}
              md={sortedPlans.length === 1 ? 6 : sortedPlans.length === 2 ? 6 : 4}
              key={plan.id}
              sx={{ display: "flex" }}
            >
              <PlanCard
                plan={plan}
                scenario={scenario}
                currentPlan={currentPlan}
                recommendedId={recommendedId}
                billing={billing}
                currSym={currSym}
                onSelect={onPlanSelect}
                onRenewCurrentPlan={onRenewCurrentPlan}
              />
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            textAlign: "center",
            fontSize: "12px",
            color: "#94a3b8",
            mt: "20px",
            px: "16px",
          }}
        >
          Prices exclude applicable taxes. Plan changes are prorated and your term dates update automatically.
        </Box>
      </Box>
    </Box>
  );
};

export default ConsolidatedPlansScreen;
